<script setup lang="ts">
/**
 * 渐进式启用占位（B.45 P1）:5 一级菜单下的未开通 capability / 未实装屏共用。
 *
 * 两种 status:
 *   * 'upgrade' —— capability 未开通,展示"未开通"+ 升级套餐 CTA。
 *   * 'wip'     —— capability 已开通但 view 还没实装(P2/P3 排期),展示"实装中"
 *                  + 排期参考链接。
 *
 * 视觉跟 prototypes (2).html 屏 11+ 的留白卡片对齐:大留白 + 单一 CTA + 引导
 * 文案。不放表单 / 不放假数据。
 */
import { computed } from 'vue'
import type { CapabilityName } from '@/api/me'
import { useAuthStore } from '@/stores/auth'
import { useSupport } from '@/composables/useSupport'

const { openSupport } = useSupport()

const props = withDefaults(
  defineProps<{
    title: string
    capability?: CapabilityName
    /** 短 phase 标签如 'B.45 P2' / 'Phase F' / 'Phase G';wip 时显示 */
    milestone?: string
    /** 强制 wip(已开通也想展示"实装中") */
    forceWip?: boolean
  }>(),
  { forceWip: false }
)

const auth = useAuthStore()

const status = computed<'upgrade' | 'wip'>(() => {
  if (props.forceWip) return 'wip'
  if (!props.capability) return 'wip'
  return auth.hasCapability(props.capability) ? 'wip' : 'upgrade'
})

const planLabel = computed(() => {
  // 渐进式启用 / 分层套餐:基础 / 经营 / 全能 + 自媒体附加
  switch (props.capability) {
    case 'business':
      return '经营版'
    case 'miniapp':
      return '全能版'
    case 'image_search':
      return '搜图版 / Pro'
    case 'media_assistant':
      return '+自媒体附加'
    default:
      return '更高套餐'
  }
})
</script>

<template>
  <section class="placeholder">
    <header class="head">
      <h1>{{ title }}</h1>
      <span v-if="status === 'upgrade'" class="pill pill-warn">
        <i class="ti ti-lock" aria-hidden="true"></i> 未开通
      </span>
      <span v-else class="pill pill-info">
        <i class="ti ti-tools" aria-hidden="true"></i> 实装中
      </span>
    </header>

    <div class="card">
      <template v-if="status === 'upgrade'">
        <div class="icon-bg">
          <i class="ti ti-sparkles" aria-hidden="true"></i>
        </div>
        <h2>升级 {{ planLabel }} 解锁此模块</h2>
        <p>
          您当前的套餐还没开通"{{ title }}"模块。升级 {{ planLabel }} 后即可使用
          完整功能,数据无缝接续。
        </p>
        <button class="cta" type="button" @click="openSupport">
          <i class="ti ti-arrow-up-circle" aria-hidden="true"></i>
          联系销售升级
        </button>
        <div class="hint">点上方按钮联系客服开通,数据无缝接续</div>
      </template>

      <template v-else>
        <div class="icon-bg">
          <i class="ti ti-rocket" aria-hidden="true"></i>
        </div>
        <h2>{{ title }} 实装中</h2>
        <p>
          套餐已开通,功能<span v-if="milestone"> {{ milestone }}</span> 排期实装,敬请期待。
        </p>
      </template>
    </div>
  </section>
</template>

<style scoped>
.placeholder {
  max-width: 960px;
  margin: 0 auto;
  padding: 24px;
}

.head {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 24px;
}
.head h1 {
  font-size: var(--fs-stat);
  font-weight: var(--fw-med);
  margin: 0;
  color: var(--color-text-primary);
}
.pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: var(--fs-label);
  padding: 3px 10px;
  border-radius: var(--border-radius-pill);
  font-weight: var(--fw-med);
}
.pill-warn {
  background: var(--color-background-warning);
  color: var(--color-text-warning);
}
.pill-info {
  background: var(--color-background-info);
  color: var(--color-text-info);
}

.card {
  background: var(--color-background-primary);
  border: 0.5px solid var(--color-border-tertiary);
  border-radius: var(--border-radius-lg);
  padding: 60px 40px;
  text-align: center;
}

.icon-bg {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--color-background-secondary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
}
.icon-bg i {
  font-size: 28px;
  color: var(--color-text-tertiary);
}

.card h2 {
  font-size: var(--fs-page);
  font-weight: var(--fw-med);
  color: var(--color-text-primary);
  margin: 0 0 10px;
}
.card p {
  font-size: var(--fs-item);
  color: var(--color-text-secondary);
  line-height: 1.7;
  margin: 0 auto 18px;
  max-width: 480px;
}

.cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  font-size: var(--fs-item);
  font-weight: var(--fw-med);
  background: var(--color-brand, var(--color-text-info));
  color: white;
  border: none;
  border-radius: var(--border-radius-md);
  cursor: pointer;
}
.cta:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.hint {
  margin-top: 12px;
  font-size: var(--fs-label);
  color: var(--color-text-tertiary);
}
</style>
